import type { AppFieldsFile, AppScreen, AppScreensFile } from '@taki/data-model';
import { alignerSurLesJeux, ecranDeclare, jeuxDeLOrg, lireChampsDeclares, lireEcransDeclares, migrerEcransDeclares, porteDeplacee, sansChampsMasques } from '@taki/data-model';
import type { DefinitionDeJeu } from '@taki/data-model';
import type { AppDeclaree, FicheApp } from './app-declaree.js';

/**
 * LA DECLARATION EFFECTIVE D'UNE APP : celle du depot, ou celle de l'org.
 *
 * LE PROBLEME QU'IL FERME. Une app MyTaki est un seul bundle pour N clients
 * (AGENTS.md section 1). Sa declaration, elle, doit pouvoir differer d'un client
 * a l'autre : un champ de plus, une porte renommee, une colonne retiree. Tant
 * que l'ecran lisait le `taki.screens.json` du depot, la seule facon de servir
 * deux clients differemment etait de forker l'app.
 *
 * CE QUE CE MODULE FAIT, ET RIEN DE PLUS : il CHOISIT entre la declaration
 * NATIVE (celle que le depot embarque) et une INSTANCE (celle que l'org a
 * ecrite), et il le fait par une fonction PURE. D'ou l'instance ARRIVE n'est pas
 * son affaire : ici elle est passee en argument, et c'est le chantier suivant
 * (`app-studio-client`) qui branchera le port Firestore qui la lit.
 *
 * UNE INSTANCE ILLISIBLE NE CASSE RIEN, ET C'EST LA SEULE VRAIE DECISION DE CE
 * MODULE. Un client qui se trompe dans son Studio ne doit pas perdre son app :
 * on retombe sur la NATIVE, et la raison du refus (le message de Zod, en clair)
 * remonte avec le resultat. L'ecran la journalise, le panneau de reglages
 * l'affiche. Personne ne se retrouve devant un ecran blanc sans explication, et
 * personne ne travaille sur une declaration cassee sans le savoir.
 */

/**
 * CE QU'UNE ORG ECRIT DE SON COTE : les deux fichiers de declaration, et ce
 * qu'elle veut changer de la fiche. Les trois sont BRUTS (`unknown`) : ils
 * viennent d'un document Firestore, donc rien n'en est verifie avant la lecture.
 */
export interface DeclarationInstance {
  champs: unknown;
  ecrans: unknown;
  /**
   * CE QUE L'ORG CHANGE DE LA FICHE, et rien d'autre : le nom affiche, la
   * description, l'icone, le libelle de l'entree de menu. L'`id`, les
   * `entities` et la `route` ne s'y trouvent pas : ils designent des DONNEES et
   * des adresses, pas des mots, et les laisser bouger par org romprait les
   * portes d'acces et les liens partages.
   */
  fiche?: {
    name?: string;
    description?: string;
    icon?: string;
    menu?: { label?: string };
  };
}

/** Les declarations d'instance de l'org courante, par id d'app. */
export type InstancesDeclarations = Readonly<Record<string, DeclarationInstance>>;

/** La declaration que l'ecran monte vraiment. */
export interface DeclarationEffective {
  champs: AppFieldsFile;
  ecrans: AppScreensFile;
  ecran: AppScreen;
  fiche: FicheApp;
}

/** Le resultat de la resolution : ce qu'on monte, d'ou ca vient, et pourquoi. */
export interface ResolutionDeDeclaration {
  declaration: DeclarationEffective;
  origine: 'native' | 'instance';
  /** La raison du repli sur la native. Absente quand rien n'a ete refuse. */
  raison?: string;
}

/** La declaration NATIVE, celle que le depot embarque. */
function native(app: AppDeclaree): DeclarationEffective {
  return { champs: app.champs, ecrans: app.ecrans, ecran: app.ecran, fiche: app.fiche };
}

/**
 * LA FICHE EFFECTIVE : celle du depot, avec les MOTS que l'org a changes.
 *
 * `id`, `menu.route` et `entities` restent ceux de la native, et il ne s'agit
 * pas d'une precaution : l'id est la clef de `installedApps` et des preferences
 * du poste, la route est une adresse partageable, et les entites sont ce dont
 * les rules derivent les portes d'acces (`docs/ACCES.md`). Un client renomme son
 * app, il ne deplace pas ses donnees.
 */
function ficheEffective(app: AppDeclaree, instance: DeclarationInstance): FicheApp {
  const mots = instance.fiche ?? {};
  return {
    ...app.fiche,
    ...(mots.name === undefined ? {} : { name: mots.name }),
    ...(mots.description === undefined ? {} : { description: mots.description }),
    ...(mots.icon === undefined ? {} : { icon: mots.icon }),
    menu: {
      ...app.fiche.menu,
      ...(mots.menu?.label === undefined ? {} : { label: mots.menu.label }),
    },
  };
}

/** La declaration relue par les lecteurs du socle, comme une instance : refus = exception. */
function relire(app: AppDeclaree, brute: DeclarationInstance, origine: string): Omit<DeclarationEffective, 'fiche'> {
  const champs = lireChampsDeclares(brute.champs, `${app.id}/taki.fields.json (${origine})`);
  const ecrans = migrerEcransDeclares(lireEcransDeclares(brute.ecrans, `${app.id}/taki.screens.json (${origine})`), app.ecrans);
  if (ecrans.ecrans.length !== 1) {
    throw new Error(
      `La declaration de l'org porte ${String(ecrans.ecrans.length)} ecrans : une app en porte UN.`,
    );
  }
  const ecran = ecranDeclare(ecrans, ecrans.ecrans[0]?.id ?? '');
  // Une instance ne deplace pas les donnees de l'app (`porteDeplacee`).
  const deplacee = porteDeplacee(app.ecran, ecran);
  if (deplacee !== null) throw new Error(deplacee);
  return { champs, ecrans, ecran };
}

/**
 * LES JEUX QUE L'ORG A PERSONNALISES, lus dans toutes ses instances
 * (`jeuxDeLOrg`, `docs/socle/jeu-listes.md`). Un jeu montre par deux apps a une
 * seule definition : celle que l'org a reglee dans l'une vaut pour l'autre,
 * meme quand l'autre tourne sur son modele.
 */
export function jeuxDesInstances(instances: InstancesDeclarations | undefined): ReadonlyMap<string, DefinitionDeJeu> {
  return jeuxDeLOrg(instances ?? {});
}

/**
 * LA DECLARATION QUE CETTE ORG MONTE.
 *
 * SANS INSTANCE, LA NATIVE, et c'est le cas ordinaire : aucune lecture, aucun
 * cout, exactement le comportement d'une app du depot.
 *
 * AVEC UNE INSTANCE, ELLE EST RELUE PAR LES MEMES LECTEURS que la native
 * (`lireChampsDeclares`, `lireEcransDeclares`), et pas par une validation
 * allegee : une declaration ecrite par un client traverse la meme porte que
 * celle d'un agent, sinon c'est le client qui deviendrait le chemin par lequel
 * une declaration fausse entre.
 *
 * LES JEUX PARTAGES DE L'ORG (`jeux`, `jeuxDesInstances`) RECALENT L'UNE COMME
 * L'AUTRE (`alignerSurLesJeux`) : la liste « Service » reglee dans Timesheet
 * est celle que Projets montre. Une declaration que ce recalage casserait
 * retombe sur elle-meme, sans recalage, avec la raison.
 */
export function resoudreDeclaration(
  app: AppDeclaree,
  instance?: DeclarationInstance,
  jeux?: ReadonlyMap<string, DefinitionDeJeu>,
): ResolutionDeDeclaration {
  /* Recalee sur les jeux de l'org, puis telle qu'elle s'AFFICHE : sans les champs
     que le jeu masque, une carte entierement masquee en moins (`sansChampsMasques`). */
  const recaler = (brute: DeclarationInstance): DeclarationInstance => sansChampsMasques(alignerSurLesJeux(brute, jeux));
  if (instance === undefined) {
    const brute: DeclarationInstance = { champs: app.champs, ecrans: app.ecrans };
    const recalee = recaler(brute);
    if (recalee === brute) return { declaration: native(app), origine: 'native' };
    try {
      return { declaration: { ...relire(app, recalee, 'jeux de l\'org'), fiche: app.fiche }, origine: 'native' };
    } catch (cause) {
      return { declaration: native(app), origine: 'native', raison: cause instanceof Error ? cause.message : String(cause) };
    }
  }
  let raisonDuRecalage: string | undefined;
  const recalee = recaler(instance);
  if (recalee !== instance) {
    try {
      return { declaration: { ...relire(app, recalee, 'org'), fiche: ficheEffective(app, instance) }, origine: 'instance' };
    } catch (cause) {
      raisonDuRecalage = cause instanceof Error ? cause.message : String(cause);
    }
  }
  try {
    return {
      declaration: { ...relire(app, instance, 'org'), fiche: ficheEffective(app, instance) },
      origine: 'instance',
      ...(raisonDuRecalage === undefined ? {} : { raison: raisonDuRecalage }),
    };
  } catch (cause) {
    return {
      declaration: native(app),
      origine: 'native',
      raison: cause instanceof Error ? cause.message : String(cause),
    };
  }
}
